import { System } from '../../../engine/ecs/core/System';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';

/**
 * Environmental Animation System for Pacman-Amazon
 * Demonstrates time-based environmental animations within ECS architecture
 *
 * Features:
 * - Continuous falling leaves animation
 * - Wall moss growth effects over time
 * - Wind effects on environmental elements
 * - Jungle sparkle animations
 */
export class EnvironmentalAnimationSystem extends System {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private time: number = 0;

  // Environmental animation data
  private fallingLeaves: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    rotation: number;
    rotationSpeed: number;
    life: number;
    maxLife: number;
    type: 'leaf' | 'flower' | 'seed';
  }> = [];

  private wallMossEffects: Map<
    number,
    {
      mossProgress: number;
      dripPoints: Array<{ x: number; y: number; size: number; life: number }>;
      sparkles: Array<{
        x: number;
        y: number;
        life: number;
        intensity: number;
      }>;
    }
  > = new Map();

  private windEffect: {
    strength: number;
    direction: number;
    time: number;
  } = {
    strength: 0.5,
    direction: 0,
    time: 0,
  };

  constructor(canvas: HTMLCanvasElement) {
    super();
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;
    this.registerRequiredComponent(TransformComponent);
    this.registerRequiredComponent(SpriteComponent);
  }

  public update(deltaTime: number): void {
    // Update global time for animations
    this.time += deltaTime;

    // Update wind effect
    this.updateWindEffect(deltaTime);

    // Update blowing sand
    this.updateBlowingSand(deltaTime);

    // Update wall melting effects
    this.updateWallMeltingEffects(deltaTime);

    // Spawn new environmental elements
    this.spawnEnvironmentalElements(deltaTime);

    // Render environmental effects
    this.renderEnvironmentalEffects();
  }

  /**
   * Update wind effect - demonstrates time-based environmental animation
   */
  private updateWindEffect(deltaTime: number): void {
    this.windEffect.time += deltaTime;

    // Create gentle wind pattern using sine wave
    this.windEffect.strength = 0.3 + Math.sin(this.windEffect.time * 0.5) * 0.2;
    this.windEffect.direction = Math.sin(this.windEffect.time * 0.3) * 0.1;
  }

  /**
   * Update blowing sand
   */
  private updateBlowingSand(deltaTime: number): void {
    for (let i = this.fallingLeaves.length - 1; i >= 0; i--) {
      const leaf = this.fallingLeaves[i];

      // Apply wind effect
      leaf.vx +=
        this.windEffect.strength * this.windEffect.direction * deltaTime;
      leaf.vx *= 0.99; // Air resistance

      // Update position
      leaf.x += leaf.vx * deltaTime * 30;
      leaf.y += leaf.vy * deltaTime * 15; // speed of the sand blowing from the top
      leaf.rotation += leaf.rotationSpeed * deltaTime;

      // Reduce life
      leaf.life -= deltaTime;

      // Remove sand that's off-screen or expired
      if (leaf.y > this.canvas.height + 50 || leaf.life <= 0) {
        this.fallingLeaves.splice(i, 1);
      }
    }
  }

  /**
   * Update wall melting effects - demonstrates environmental animation over time
   */
  private updateWallMeltingEffects(deltaTime: number): void {
    for (const [entityId, effect] of this.wallMossEffects) {
      // Progress melting over time
      effect.mossProgress += deltaTime * 0.1; // Slow melting

      // Update drip points
      for (let i = effect.dripPoints.length - 1; i >= 0; i--) {
        const drip = effect.dripPoints[i];
        drip.y += deltaTime * 100; // Drips fall
        drip.life -= deltaTime;

        if (drip.life <= 0) {
          effect.dripPoints.splice(i, 1);
        }
      }

      // Update sparkles
      for (let i = effect.sparkles.length - 1; i >= 0; i--) {
        const sparkle = effect.sparkles[i];
        sparkle.life -= deltaTime;
        sparkle.intensity =
          Math.sin(this.time * 5 + sparkle.x * 0.1) * 0.5 + 0.5;

        if (sparkle.life <= 0) {
          effect.sparkles.splice(i, 1);
        }
      }

      // Add new drip points occasionally
      if (Math.random() < 0.02) {
        // 2% chance per frame
        effect.dripPoints.push({
          x: Math.random() * 24 - 12,
          y: -12,
          size: 1 + Math.random() * 2,
          life: 2 + Math.random() * 5, // Longer life (increased from 2-5 to 2-20 seconds)
        });
      }

      // Add new sparkles occasionally
      if (Math.random() < 0.01) {
        // 1% chance per frame
        effect.sparkles.push({
          x: Math.random() * 24 - 12,
          y: Math.random() * 24 - 12,
          life: 1 + Math.random() * 2,
          intensity: Math.random(),
        });
      }
    }
  }

  /**
   * Spawn new environmental elements - demonstrates continuous animation generation
   */
  private spawnEnvironmentalElements(deltaTime: number): void {
    // Spawn new falling leaves occasionally
    if (Math.random() < 0.1) {
      const types: Array<'leaf' | 'flower' | 'seed'> = [
        'leaf',
        'flower',
        'seed',
      ];

      this.fallingLeaves.push({
        x: Math.random() * this.canvas.width,
        y: -20 - Math.random() * 50,
        vx: (Math.random() - 0.5) * 20,
        vy: 30 + Math.random() * 20,
        size: 3 + Math.random() * 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 2,
        life: 3 + Math.random() * 2,
        maxLife: 3 + Math.random() * 2,
        type: types[Math.floor(Math.random() * types.length)],
      });
    }
  }

  /**
   * Initialize wall melting for an entity
   */
  public initializeWallMelting(entityId: number): void {
    this.wallMossEffects.set(entityId, {
      mossProgress: 0,
      dripPoints: [],
      sparkles: [],
    });
  }

  /**
   * Initialize wall erosion for an entity
   */
  public initializeWallErosion(entityId: number): void {
    this.wallMossEffects.set(entityId, {
      mossProgress: 0,
      dripPoints: [],
      sparkles: [],
    });
  }

  /**
   * Render environmental effects
   */
  private renderEnvironmentalEffects(): void {
    this.renderBlowingSand();
    this.renderWallMeltingEffects();
  }

  /**
   * Render falling leaves
   */
  private renderBlowingSand(): void {
    for (const leaf of this.fallingLeaves) {
      this.context.save();
      this.context.translate(leaf.x, leaf.y);
      this.context.rotate(leaf.rotation);
      this.context.globalAlpha = leaf.life / leaf.maxLife;

      switch (leaf.type) {
        case 'leaf':
          this.renderLeaf(leaf.size);
          break;
        case 'flower':
          this.renderFlower(leaf.size);
          break;
        case 'seed':
          this.renderSeed(leaf.size);
          break;
      }

      this.context.restore();
    }
  }

  /**
   * Render a leaf particle
   */
  private renderLeaf(size: number): void {
    this.context.fillStyle = '#228B22'; // Forest green
    this.context.beginPath();
    this.context.ellipse(0, 0, size, size * 0.5, 0, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render a flower particle
   */
  private renderFlower(size: number): void {
    this.context.fillStyle = '#FF69B4'; // Hot pink
    this.context.beginPath();
    this.context.arc(0, 0, size, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render a seed particle
   */
  private renderSeed(size: number): void {
    this.context.fillStyle = '#8B4513'; // Saddle brown
    this.context.beginPath();
    this.context.arc(0, 0, size * 0.5, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render wall melting effects
   */
  private renderWallMeltingEffects(): void {
    for (const [entityId, effect] of this.wallMossEffects) {
      // Render drip points
      for (const drip of effect.dripPoints) {
        this.context.save();
        this.context.globalAlpha = (drip.life / 5) * 0.6; // More subtle
        this.context.fillStyle = '#c7ebfc';
        this.context.strokeStyle = '#c7ebfc';

        //   this.context.fillStyle = '#81D4FA';
        // this.context.strokeStyle = '#81D4FA';
        this.context.lineWidth = drip.size * 0.8;
        this.context.beginPath();
        this.context.moveTo(drip.x, drip.y - drip.size * 1.5);
        this.context.lineTo(drip.x, drip.y + drip.size * 1.5);
        this.context.stroke();
        this.context.restore();
      }

      // Render sparkles
      for (const sparkle of effect.sparkles) {
        this.context.save();
        this.context.globalAlpha = (sparkle.life / 3) * sparkle.intensity;
        this.context.fillStyle = '#FFFFFF';
        this.context.shadowColor = '#B3E5FC';
        this.context.shadowBlur = 8;
        this.context.beginPath();
        this.context.arc(sparkle.x, sparkle.y, 2, 0, Math.PI * 2);
        this.context.fill();
        this.context.restore();
      }
    }
  }

  /**
   * Get environmental animation data for rendering system
   */
  public getEnvironmentalData(): {
    fallingLeaves: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      rotation: number;
      rotationSpeed: number;
      life: number;
      maxLife: number;
      type: 'leaf' | 'flower' | 'seed';
    }>;
    wallMossEffects: Map<
      number,
      {
        mossProgress: number;
        dripPoints: Array<{ x: number; y: number; size: number; life: number }>;
        sparkles: Array<{
          x: number;
          y: number;
          life: number;
          intensity: number;
        }>;
      }
    >;
    windEffect: {
      strength: number;
      direction: number;
      time: number;
    };
  } {
    return {
      fallingLeaves: this.fallingLeaves,
      wallMossEffects: this.wallMossEffects,
      windEffect: this.windEffect,
    };
  }
}
